import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MAPPING_FIELDS,
  applyTemplate,
  guessMapping,
  mapStatementRows,
  toTemplate,
  validateMapping,
  type ExpenseAccount,
  type MappedStatement,
  type MappingField,
  type StatementMapping,
} from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import type { StatementFile } from "../lib/statementFile";
import type { StatementImportPayload } from "../services/statementsService";
import { money } from "../lib/format";
import { ui } from "../theme";
import { Field, Notice, Spinner } from "./ui";

// Mapeo de columnas de un estado de cuenta en CSV/Excel y vista previa (#953).
// Un OFX no necesita mapeo: trae fecha, importe con signo y descripción.

const NS = "expensesStatements";
const PREVIEW_ROWS = 8;

export default function StatementMapper({
  fileName,
  parsed,
  account,
  busy,
  onCancel,
  onSubmit,
}: {
  fileName: string;
  parsed: StatementFile;
  account: ExpenseAccount;
  busy?: boolean;
  onCancel: () => void;
  onSubmit: (payload: Omit<StatementImportPayload, "accountId">) => void;
}) {
  const { t, i18n } = useTranslation(["common", NS]);
  const tt = (k: string, o?: Record<string, unknown>) => t(`${NS}:${k}`, o);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);

  const rows = useMemo<unknown[][]>(() => (parsed.format === "ofx" ? [] : parsed.rows), [parsed]);
  const fromTemplate = useMemo(() => (parsed.format === "ofx" ? null : applyTemplate(rows, account.importTemplate)), [parsed, rows, account.importTemplate]);
  const [mapping, setMapping] = useState<StatementMapping>(() => fromTemplate || guessMapping(rows));
  const [saveTemplate, setSaveTemplate] = useState(true);

  const mapped: MappedStatement = useMemo(() => {
    if (parsed.format === "ofx") {
      const r = parsed.ofx.rows;
      const dates = r.map((x) => x.date).sort();
      return {
        rows: r,
        errors: [],
        period: parsed.ofx.period.from ? parsed.ofx.period : { from: dates[0] || null, to: dates[dates.length - 1] || null },
        totals: { debits: {}, credits: {} },
      };
    }
    return mapStatementRows(rows, mapping, account.currency || "MXN");
  }, [parsed, rows, mapping, account.currency]);

  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [declared, setDeclared] = useState("");
  const period = { from: from || mapped.period.from, to: to || mapped.period.to };

  const missing = parsed.format === "ofx" ? [] : validateMapping(mapping);
  const width = Math.max(0, ...rows.slice(0, 40).map((r) => r.length));
  const header = mapping.headerRow >= 0 ? rows[mapping.headerRow] || [] : [];
  const columnLabel = (i: number) => {
    const h = header[i];
    const text = h == null ? "" : String(h).trim();
    return text ? `${text}` : tt("import.mapper.column", { n: i + 1 });
  };
  const setColumn = (f: MappingField, v: string) =>
    setMapping((m) => {
      const columns = { ...m.columns };
      if (v === "") delete columns[f];
      else columns[f] = Number(v);
      return { ...m, columns };
    });
  const singleAmount = mapping.columns.amount !== undefined && mapping.columns.debit === undefined && mapping.columns.credit === undefined;
  const ofxMismatch = parsed.format === "ofx" && parsed.ofx.last4 && account.last4 && parsed.ofx.last4 !== account.last4;

  const submit = () => {
    const total = declared.trim() === "" ? null : Number(declared.replace(/[$,\s]/g, ""));
    onSubmit({
      format: parsed.format,
      rows: mapped.rows,
      statementPeriod: period.from && period.to ? { from: period.from, to: period.to } : null,
      statementTotal: total != null && Number.isFinite(total) ? total : null,
      template: parsed.format === "ofx" ? null : toTemplate(rows, mapping, new Date().toISOString()),
      saveTemplate: parsed.format !== "ofx" && saveTemplate,
    });
  };

  return (
    <div className={`${c.card} space-y-4`} data-testid="statement-mapper">
      <div>
        <h3 className={`text-sm font-semibold ${c.text}`}>
          {parsed.format === "ofx" ? fileName : tt("import.mapper.title")}
          <span className={`ml-2 text-xs font-normal ${c.muted}`}>{tt("import.withoutAi")}</span>
        </h3>
        {parsed.format !== "ofx" && <p className={`text-xs ${c.muted}`}>{tt("import.mapper.subtitle")}</p>}
      </div>

      {fromTemplate && <Notice tone="ok">{tt("import.mapper.templateApplied")}</Notice>}
      {parsed.format === "ofx" && (
        <Notice>
          {tt("import.mapper.ofxInfo", {
            type: parsed.ofx.accountType ? tt(`types.${parsed.ofx.accountType}`) : "",
            account: parsed.ofx.accountNumber || "—",
            count: parsed.ofx.rows.length,
          })}
        </Notice>
      )}
      {ofxMismatch && <Notice tone="warn">{tt("import.mapper.ofxOtherAccount", { last4: parsed.ofx.last4 })}</Notice>}

      {parsed.format !== "ofx" && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Field label={tt("import.mapper.headerRow")}>
            <select
              aria-label="header-row"
              className={c.input}
              value={mapping.headerRow}
              onChange={(e) => setMapping((m) => ({ ...m, headerRow: Number(e.target.value) }))}
            >
              <option value={-1}>{tt("import.mapper.noHeader")}</option>
              {rows.slice(0, 20).map((r, i) => (
                <option key={i} value={i}>
                  {tt("import.mapper.row", { n: i + 1 })}: {r.slice(0, 3).map((x) => String(x ?? "")).join(" · ").slice(0, 40)}
                </option>
              ))}
            </select>
          </Field>
          {MAPPING_FIELDS.map((f) => (
            <Field key={f} label={tt(`import.mapper.fields.${f}`)}>
              <select aria-label={`map-${f}`} className={c.input} value={mapping.columns[f] ?? ""} onChange={(e) => setColumn(f, e.target.value)}>
                <option value="">{tt("import.mapper.none")}</option>
                {Array.from({ length: width }, (_, i) => (
                  <option key={i} value={i}>
                    {columnLabel(i)}
                  </option>
                ))}
              </select>
            </Field>
          ))}
          <Field label={tt("import.mapper.dateOrder")}>
            <select aria-label="date-order" className={c.input} value={mapping.dateOrder} onChange={(e) => setMapping((m) => ({ ...m, dateOrder: e.target.value as StatementMapping["dateOrder"] }))}>
              {(["dmy", "mdy", "ymd"] as const).map((o) => (
                <option key={o} value={o}>
                  {tt(`import.mapper.dateOrders.${o}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={tt("import.mapper.decimal")}>
            <select aria-label="decimal" className={c.input} value={mapping.decimal} onChange={(e) => setMapping((m) => ({ ...m, decimal: e.target.value as StatementMapping["decimal"] }))}>
              <option value=".">{tt("import.mapper.decimals.dot")}</option>
              <option value=",">{tt("import.mapper.decimals.comma")}</option>
            </select>
          </Field>
          {singleAmount && (
            <Field label={tt("import.mapper.amountSign")} className="col-span-2">
              <select aria-label="amount-sign" className={c.input} value={mapping.amountSign} onChange={(e) => setMapping((m) => ({ ...m, amountSign: e.target.value as StatementMapping["amountSign"] }))}>
                {(["cargo_negativo", "cargo_positivo"] as const).map((o) => (
                  <option key={o} value={o}>
                    {tt(`import.mapper.amountSigns.${o}`)}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
      )}

      {missing.length > 0 && <Notice tone="warn">{tt("import.mapper.missing", { fields: missing.map((f) => tt(`import.mapper.fields.${f}`)).join(", ") })}</Notice>}

      <div>
        <p className={`mb-1 text-xs font-semibold ${c.muted}`}>
          {tt("import.mapper.preview")} · {tt("import.mapper.rows", { count: mapped.rows.length })}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm" data-testid="mapper-preview">
            <thead>
              <tr className={`text-left text-xs ${c.muted}`}>
                <th className="py-1 pr-2">{tt("import.mapper.fields.date")}</th>
                <th className="py-1 pr-2">{tt("import.mapper.fields.description")}</th>
                <th className="py-1 pr-2 text-right">{tt("import.mapper.fields.debit")}</th>
                <th className="py-1 pr-2 text-right">{tt("import.mapper.fields.credit")}</th>
              </tr>
            </thead>
            <tbody>
              {mapped.rows.slice(0, PREVIEW_ROWS).map((r) => (
                <tr key={r.line} className={`border-t ${c.border} ${c.text}`}>
                  <td className="whitespace-nowrap py-1 pr-2 tabular-nums">{r.date}</td>
                  <td className="max-w-xs truncate py-1 pr-2" title={r.description}>
                    {r.description}
                  </td>
                  <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums">{r.isCredit ? "" : money(r.amount, r.currency, i18n.language)}</td>
                  <td className="whitespace-nowrap py-1 pr-2 text-right tabular-nums text-emerald-600">{r.isCredit ? money(r.amount, r.currency, i18n.language) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {mapped.errors.length > 0 && (
        <Notice tone="warn">
          {tt("import.mapper.errors", {
            count: mapped.errors.length,
            lines: mapped.errors
              .slice(0, 10)
              .map((e) => e.line)
              .join(", "),
          })}
        </Notice>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label={`${tt("import.period")} · ${tt("import.from")}`}>
          <input type="date" aria-label="period-from" className={c.input} value={period.from || ""} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={tt("import.to")}>
          <input type="date" aria-label="period-to" className={c.input} value={period.to || ""} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Field label={tt("import.declaredTotal")}>
          <input aria-label="declared-total" inputMode="decimal" className={c.input} value={declared} onChange={(e) => setDeclared(e.target.value)} placeholder={tt("import.declaredHint")} />
        </Field>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        {parsed.format !== "ofx" ? (
          <label className={`flex items-center gap-2 text-sm ${c.muted}`}>
            <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={saveTemplate} onChange={(e) => setSaveTemplate(e.target.checked)} />
            {tt("import.mapper.saveTemplate")}
          </label>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          {busy && <Spinner />}
          <button type="button" className={c.secondary} onClick={onCancel} disabled={busy}>
            {tt("common.cancel")}
          </button>
          <button type="button" className={c.primary} disabled={busy || missing.length > 0 || mapped.rows.length === 0} onClick={submit}>
            {tt("import.mapper.continue", { count: mapped.rows.length })}
          </button>
        </div>
      </div>
    </div>
  );
}
